import {
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';

import { fetchSuppliersOverviewCounts } from './fetchSuppliersOverviewCounts';
import {
  type SuppliersOverviewCountsQueryParams,
  type SuppliersOverviewCountsResponse,
} from './types';

/**
 * Generates the query options for the suppliers overview counts query so they
 * can be shared between the useQuery hook and prefetching in route loaders.
 *
 * @param queryParams - Optional filter parameters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and staleTime.
 */
export const getSuppliersOverviewCountsQueryOptions = <
  TData = SuppliersOverviewCountsResponse,
>({
  queryParams,
  options,
}: {
  queryParams: Partial<SuppliersOverviewCountsQueryParams>;
  options?: Omit<
    UseQueryOptions<SuppliersOverviewCountsResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: queryParams.recipientCompanyIds?.length
      ? async ({ signal }) =>
          fetchSuppliersOverviewCounts(queryParams, { signal })
      : skipToken,
    queryKey: queryKeysSupplier.getOverviewCounts(queryParams),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });

/**
 * React Query based custom hook for fetching per-status-group supplier counts.
 *
 * Returns the total number of suppliers per status group (completely_active,
 * partially_active, activation_possible, integration_planned) matching the given filter parameters.
 *
 * @param queryParams - Optional filter parameters.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook containing the status-group counts.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/get_suppliers_overview_counts_suppliers_counts_get
 */
export const useQuerySuppliersOverviewCounts = <
  TData = SuppliersOverviewCountsResponse,
>(
  queryParams: Partial<SuppliersOverviewCountsQueryParams> = {},
  options?: Omit<
    UseQueryOptions<SuppliersOverviewCountsResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getSuppliersOverviewCountsQueryOptions<TData>({ queryParams, options }),
  );
